import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { getSurvey, getQuota, getSubmissionByToken, submitSurvey, updateSubmissionByToken } from '../lib/api';
import type { SurveyConfig, Question } from '../components/editor/types';
import { isNonAnswerType, titleText, optionTextsOf, withOptionText, selectLimitError, textFormatError, questionImages, OPTION_TEXT_KEY, MATRIX_COL_TEXT_KEY, matrixColTextsOf, withMatrixColText, allowedOptionsOf, pruneOptionLinkAnswers, matrixLikeAnswered } from '../components/editor/types';
import { computeVisibleQuestions, computeJumpPath } from '../lib/display-logic-bridge';
import QuestionInput from '../components/runner/QuestionInput';
import { reportInvalidScore } from '../components/runner/ScoreInput';
import SignaturePad from '../components/runner/SignaturePad';
import DraftRecoveryPrompt from '../components/runner/DraftRecoveryPrompt';
import ImageLightbox from '../components/ImageLightbox';
import RichTextView from '../components/editor/RichTextView';
import { paginateQuestions, displayIndexMap } from '../components/editor/question-utils';

type Mode = 'fill' | 'done' | 'edit';

type Props = {
  mode?: Mode;
};

type PendingDraft = {
  config?: SurveyConfig;
  revision?: string;
  answers: Record<string, any>;
  prefaceChecked: boolean;
  pageIndex: number;
  lastQuestionId?: string;
};

type QuotaState = {
  total: { limit: number | null; used: number; remaining: number | null };
  options: Array<{
    questionId: string;
    optionValue: string;
    limit: number;
    used: number;
    remaining: number;
    onQuotaFull: 'jumpNext' | 'end';
  }>;
};

export default function Runner({ mode = 'fill' }: Props) {
  const { id, token } = useParams<{ id: string; token: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [config, setConfig] = useState<SurveyConfig | null>(null);
  const [revision, setRevision] = useState<string | undefined>();
  const [surveyId, setSurveyId] = useState<string | null>(null);
  const [quota, setQuota] = useState<QuotaState | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [signature, setSignature] = useState<string | null>(null);
  const [prefaceChecked, setPrefaceChecked] = useState(false);
  const [titleImage, setTitleImage] = useState<{ src: string; alt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // 分页页码：必须在任何提前 return 之前声明，否则前置页/加载态渲染的 Hook 数量少于正文，
  // React 会抛 #310（Rendered more hooks than during the previous render）导致整页白屏。
  const [pageIndex, setPageIndex] = useState(0);
  const [lastQuestionId, setLastQuestionId] = useState<string | null>(null);
  const questionsRef = useRef<HTMLOListElement>(null);
  const [attemptedPage, setAttemptedPage] = useState<number | null>(null);
  const [navigationTarget, setNavigationTarget] = useState<{ questionId?: string; attempt: number } | null>(null);
  const locateQuestion = (questionId?: string) => setNavigationTarget(previous => ({ questionId, attempt: (previous?.attempt ?? 0) + 1 }));

  // Run after the new page (or missing-answer hint) is committed, not against the old DOM.
  // Focus the question wrapper so mobile navigation does not open the software keyboard.
  useLayoutEffect(() => {
    const list = questionsRef.current;
    if (!list || !navigationTarget) return;
    const target = navigationTarget.questionId
      ? Array.from(list.querySelectorAll<HTMLElement>('[data-question-id]')).find(item => item.dataset.questionId === navigationTarget.questionId)
      : list.querySelector<HTMLElement>('.rq-item:not(.is-non-answer)') ?? list.querySelector<HTMLElement>('.rq-item');
    const anchor = target ?? list;
    anchor.focus({ preventScroll: true });
    anchor.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [navigationTarget]);
  const quotaPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [geoState, setGeoState] = useState<{ status: 'idle' | 'loading' | 'ok' | 'blocked' | 'error'; lat?: number; lng?: number; accuracy?: number; distance?: number; radius?: number; reason?: string }>({ status: 'idle' });
  // 先等待用户选择，再回填；选择前禁止自动保存覆盖原草稿。
  const [pendingDraft, setPendingDraft] = useState<PendingDraft | null>(null);
  const [draftChecked, setDraftChecked] = useState(false);
  const draftDecisionRef = useRef(false);
  const [draftNotice, setDraftNotice] = useState(false);

  useLayoutEffect(() => {
    if (pendingDraft || !draftDecisionRef.current) return;
    draftDecisionRef.current = false;
    const start = document.querySelector<HTMLElement>('.runner h1, .runner .preface-page');
    if (start) {
      start.setAttribute('tabindex', '-1');
      start.focus({ preventScroll: true });
    }
    window.scrollTo(0, 0);
  }, [pendingDraft]);

  // 加载
  useEffect(() => {
    let cancelled = false;
    setConfig(null);
    setRevision(undefined);
    setSurveyId(null);
    setAnswers({});
    setSignature(null);
    setPrefaceChecked(false);
    setPageIndex(0);
    setLastQuestionId(null);
    setAttemptedPage(null);
    setNavigationTarget(null);
    setError(null);
    setPendingDraft(null);
    setDraftChecked(false);
    setDraftNotice(false);
    (async () => {
      try {
        if (mode === 'edit' && token) {
          // 改答：先取 submission，再取 survey
          const subResp = await getSubmissionByToken(token);
          if (cancelled) return;
          const sub = subResp.submission;
          setSurveyId(sub.surveyId);
          setAnswers(JSON.parse(sub.payload || '{}'));
          setSignature(sub.signature);
          const sr = subResp.config ? subResp : await getSurvey(sub.surveyId, subResp.revision);
          if (cancelled) return;
          setConfig(sr.config);
          setRevision(sr.revision);
        } else if (mode === 'fill' && id) {
          setSurveyId(id);
          const sr = await getSurvey(id);
          if (cancelled) return;
          setConfig(sr.config);
          setRevision(sr.revision);
          // 草稿待选：仅 fill 模式且未显式关闭（draftRecovery 默认开启）。
          // 超过 7 天或问卷已结束则清掉；此处只读取，不自动回填答案或页码。
          if (sr.config?.settings?.draftRecovery !== false) {
            try {
              const raw = localStorage.getItem(`wj_draft_${id}`);
              if (raw) {
                const d = JSON.parse(raw);
                if (d && typeof d === 'object') {
                  const expired = typeof d.ts === 'number' && Date.now() - d.ts > DRAFT_TTL;
                  const tl = sr.config.settings?.timeLimit;
                  const surveyEnded = !!(tl?.end && new Date(tl.end).getTime() < Date.now());
                  if (expired || surveyEnded) {
                    localStorage.removeItem(`wj_draft_${id}`);
                  } else if (!cancelled) {
                    const saved = d.revision && d.revision !== sr.revision ? await getSurvey(id, d.revision).catch((e) => {
                      setConfig(null);
                      throw Object.assign(e instanceof Error ? e : new Error('原问卷版本加载失败，请重试'), { draftVersionError: true });
                    }) : sr;
                    if (cancelled) return;
                    const cleanAns = sanitizeDraftAnswers(saved.config, d.answers ?? {});
                    // 0907 选项关联：草稿里目标题的勾选若已不在允许范围内（问卷改过配置）一并清掉
                    const restoredAnswers = pruneOptionLinkAnswers(saved.config, cleanAns, new Set(computeVisibleQuestions(saved.config, cleanAns)));
                    const questions = [...saved.config.questions, ...(saved.config.settings?.preface?.questions ?? [])];
                    const savedQuestionId = typeof d.lastQuestionId === 'string' && questions.some(q => q.id === d.lastQuestionId && !isNonAnswerType(q.type)) ? d.lastQuestionId : undefined;
                    if (Object.keys(restoredAnswers).length > 0 || savedQuestionId) {
                      setPendingDraft({
                        config: saved.config,
                        revision: saved.revision,
                        answers: restoredAnswers,
                        prefaceChecked: d.prefaceChecked === true,
                        pageIndex: typeof d.pageIndex === 'number' && Number.isFinite(d.pageIndex) ? Math.max(0, Math.floor(d.pageIndex)) : 0,
                        lastQuestionId: savedQuestionId,
                      });
                    }
                  }
                }
              }
            } catch (e: any) { if (e?.status || e?.draftVersionError) { setConfig(null); throw e; } }
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || '加载失败');
      } finally {
        if (!cancelled) setDraftChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, id, token]);

  // 配额轮询（10 秒一次刷新，让"已满"状态实时反映）
  useEffect(() => {
    if (mode === 'done' || !surveyId) return;
    let cancelled = false;
    const fetchQuota = () =>
      getQuota(surveyId)
        .then((q: QuotaState) => !cancelled && setQuota(q))
        .catch(() => {});
    fetchQuota();
    quotaPollRef.current = setInterval(fetchQuota, 10000);
    return () => {
      cancelled = true;
      if (quotaPollRef.current) clearInterval(quotaPollRef.current);
    };
  }, [mode, surveyId]);

  // 地理位置签到：取本机坐标与锚点比对（改答模式跳过——用户离开现场后回填）
  useEffect(() => {
    const gf = config?.settings?.geoFence;
    if (mode === 'done' || mode === 'edit' || !gf?.enabled || gf.lat == null || gf.lng == null) {
      setGeoState((prev) => (prev.status === 'idle' ? prev : { status: 'idle' }));
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoState({ status: 'error', reason: '当前浏览器不支持定位功能' });
      return;
    }
    let cancelled = false;
    setGeoState({ status: 'loading' });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const { latitude, longitude, accuracy } = pos.coords;
        const radius = gf.radius ?? 100;
        const maxAcc = gf.maxAccuracy ?? 100;
        if (accuracy != null && accuracy > maxAcc) {
          setGeoState({ status: 'error', reason: `定位精度 ${Math.round(accuracy)} 米过低（上限 ${maxAcc} 米），请到空旷处重试` });
          return;
        }
        const d = haversine(gf.lat as number, gf.lng as number, latitude, longitude);
        setGeoState(
          d > radius
            ? { status: 'blocked', lat: latitude, lng: longitude, accuracy, distance: Math.round(d), radius }
            : { status: 'ok', lat: latitude, lng: longitude, accuracy, distance: Math.round(d), radius },
        );
      },
      (e) => {
        if (cancelled) return;
        const map: Record<string, string> = {
          1: '定位权限被拒绝，请到浏览器设置允许本站获取位置',
          2: '位置不可用（GPS 信号弱或浏览器无法获取）',
          3: '定位超时，请到空旷处重试',
        };
        setGeoState({ status: 'error', reason: map[e.code] ?? e.message ?? '定位失败' });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
    return () => { cancelled = true; };
  }, [mode, config?.settings?.geoFence?.enabled, config?.settings?.geoFence?.lat, config?.settings?.geoFence?.lng]);

  // 草稿自动保存：仅 fill 模式且未显式关闭（draftRecovery 默认开启）；
  // debounce 300ms 写入，避免每次按键都写盘；剔除 file 题（dataUrl 体积大撑爆 localStorage 5MB 上限）
  useEffect(() => {
    if (mode !== 'fill' || !surveyId || surveyId !== id || !config || !draftChecked || pendingDraft) return;
    if (config.settings?.draftRecovery === false) return;
    const saveDraft = () => {
      try {
        const savedAnswers = sanitizeDraftAnswers(config, answers);
        // 全新空白问卷不创建草稿，重新填写后刷新也不会再次提示旧数据。
        if (Object.keys(savedAnswers).length === 0 && !prefaceChecked && pageIndex === 0 && !lastQuestionId) {
          localStorage.removeItem(`wj_draft_${surveyId}`);
          return;
        }
        localStorage.setItem(
          `wj_draft_${surveyId}`,
          JSON.stringify({
            ts: Date.now(),
            revision,
            answers: savedAnswers,
            prefaceChecked,
            pageIndex,
            lastQuestionId,
          }),
        );
      } catch {}
    };
    const saveWhenHidden = () => { if (document.visibilityState === 'hidden') saveDraft(); };
    const handle = setTimeout(saveDraft, 300);
    // 刷新/退出时补写最后一次输入与题号，不丢失尚未到 300ms 的草稿。
    window.addEventListener('pagehide', saveDraft);
    document.addEventListener('visibilitychange', saveWhenHidden);
    return () => {
      clearTimeout(handle);
      window.removeEventListener('pagehide', saveDraft);
      document.removeEventListener('visibilitychange', saveWhenHidden);
    };
  }, [mode, id, surveyId, config, revision, draftChecked, pendingDraft, answers, prefaceChecked, pageIndex, lastQuestionId]);

  // done 页：从 location.state 取 editToken + resultPage + redirectUrl
  if (mode === 'done') {
    const state = (location.state ?? {}) as {
      editToken?: string;
      surveyId?: string;
      documentUrl?: string | null;
      editAnswerEnabled?: boolean;
      resultPage?: { title?: string; body?: string };
      redirectUrl?: { enabled?: boolean; url?: string };
    };
    return (
      <DonePage
        editToken={state.editToken}
        surveyId={state.surveyId ?? id ?? null}
        documentUrl={state.documentUrl ?? null}
        editAnswerEnabled={!!state.editAnswerEnabled}
        resultPage={state.resultPage}
        redirectUrl={state.redirectUrl}
      />
    );
  }

  // 仅"问卷加载失败"用全屏错误；提交类错误在表单底部内联提示
  if (error && !config) {
    return (
      <div className="runner">
        <div className="runner-error">加载失败：{error}</div>
        <Link to="/" className="btn">返回首页</Link>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="runner">
        <div className="runner-loading">加载中…</div>
      </div>
    );
  }

  // 停止收集
  if (config.settings?.stopCollect) {
    return (
      <div className="runner">
        <div className="runner-stopped">
          <h1>问卷已停止收集</h1>
          <p>如有疑问请联系主办方。</p>
        </div>
      </div>
    );
  }

  // 每人限填一次：本机已提交过直接拦（服务端另有设备去重兜底）
  let submittedToken: string | null = null;
  try { submittedToken = surveyId ? localStorage.getItem(`wj_submitted_${surveyId}`) : null; } catch {}
  if (mode === 'fill' && config.settings?.oncePerUser && submittedToken) {
    const canEdit = !!config.settings?.editAnswer?.enabled;
    return (
      <div className="runner">
        <div className="runner-stopped">
          <h1>您已填写过本问卷</h1>
          <p>本问卷每人限填一次，感谢您的参与。</p>
          {canEdit && (
            <p style={{ marginTop: 16 }}>
              <Link className="btn btn-primary" to={`/edit/${submittedToken}`}>查看 / 修改我的提交</Link>
            </p>
          )}
        </div>
      </div>
    );
  }

  // 填写有效期（timeLimit）
  const tl = config.settings?.timeLimit;
  if (tl?.enabled && (tl.start || tl.end)) {
    const now = Date.now();
    const startMs = tl.start ? new Date(tl.start).getTime() : -Infinity;
    const endMs = tl.end ? new Date(tl.end).getTime() : Infinity;
    if (now < startMs) {
      return (
        <div className="runner">
          <div className="runner-stopped">
            <h1>问卷尚未开放</h1>
            <p>开放时间：{tl.start ? new Date(tl.start).toLocaleString('zh-CN') : '未指定'}</p>
          </div>
        </div>
      );
    }
    if (now > endMs) {
      return (
        <div className="runner">
          <div className="runner-stopped">
            <h1>问卷已结束</h1>
            <p>截止时间：{tl.end ? new Date(tl.end).toLocaleString('zh-CN') : '未指定'}</p>
          </div>
        </div>
      );
    }
  }

  // 地理位置签到拦截
  const gf = config.settings?.geoFence;
  if (gf?.enabled && gf.lat != null && gf.lng != null) {
    if (geoState.status === 'loading') {
      return (
        <div className="runner">
          <div className="runner-stopped">
            <h1>正在定位…</h1>
            <p>请允许浏览器获取您的位置以继续填写。</p>
          </div>
        </div>
      );
    }
    if (geoState.status === 'blocked') {
      return (
        <div className="runner">
          <div className="runner-stopped">
            <h1>超出签到范围</h1>
            <p>您当前位置距签到点约 <strong>{geoState.distance}</strong> 米，超出允许范围 {geoState.radius} 米。</p>
            <p>请到现场后重新扫码进入。</p>
          </div>
        </div>
      );
    }
    const onErr = (gf as any).onError ?? 'block';
    if (geoState.status === 'error' && onErr === 'block') {
      return (
        <div className="runner">
          <div className="runner-stopped">
            <h1>无法获取您的位置</h1>
            <p>{geoState.reason}</p>
            <p>本问卷要求现场签到，请检查定位权限后重试。</p>
          </div>
        </div>
      );
    }
  }

  // 总配额已满
  if (quota?.total && quota.total.limit !== null && quota.total.remaining === 0) {
    return (
      <div className="runner">
        <div className="runner-stopped">
          <h1>名额已满</h1>
          <p>本问卷名额已收集完毕，感谢您的关注。</p>
        </div>
      </div>
    );
  }

  // 选项配额处理
  const optionQuotasByQuestion = computeOptionQuotas(quota);
  const fullOptionsByQuestion = computeFullOptions(optionQuotasByQuestion); // 满额子集：onQuotaFull=end 提前结束用
  const fullEnd = isAnyFullEnd(fullOptionsByQuestion); // 任一选项 onQuotaFull=end 且满额 → 整问卷结束

  if (fullEnd) {
    return (
      <div className="runner">
        <div className="runner-stopped">
          <h1>该问卷已结束</h1>
          <p>某选项配额已满，按规则提前结束。</p>
        </div>
      </div>
    );
  }

  if (mode === 'fill' && pendingDraft) {
    const chooseDraft = (restore: boolean) => {
      const position = restore ? resumeDraftPosition(pendingDraft.config ?? config, pendingDraft) : { pageIndex: 0, questionId: undefined };
      if (restore && pendingDraft.config) { setConfig(pendingDraft.config); setRevision(pendingDraft.revision); }
      if (!restore && surveyId) {
        try { localStorage.removeItem(`wj_draft_${surveyId}`); } catch {}
      }
      setAnswers(restore ? pendingDraft.answers : {});
      setPrefaceChecked(restore ? pendingDraft.prefaceChecked : false);
      setPageIndex(position.pageIndex);
      setLastQuestionId(position.questionId ?? null);
      setSignature(null);
      setAttemptedPage(null);
      if (position.questionId) locateQuestion(position.questionId);
      else setNavigationTarget(null);
      setError(null);
      setDraftNotice(restore);
      draftDecisionRef.current = !position.questionId;
      setPendingDraft(null);
    };
    return (
      <div className="runner">
        <div className="runner-head"><h1>{config.header.mainTitle}</h1></div>
        <DraftRecoveryPrompt onRestore={() => chooseDraft(true)} onRestart={() => chooseDraft(false)} />
      </div>
    );
  }

  // 计算可见题目（选项满 + jumpNext 的题视为跳过：从 config 临时移除？不，让用户继续，但 disable 那选项）
  // 简化：visible 题正常显示，但满额选项 disable
  // 0904 起跳题逻辑：path = 实际遍历到的题；skipped = 被跳过的题（提交时由后端裁剪答案）
  const visibleIds = computeVisibleQuestions(config, answers);
  const visibleIdSet = new Set(visibleIds);
  const visible = config.questions.filter((q) => visibleIds.includes(q.id));
  const { path: jumpPath, skipped: jumpSkipped } = computeJumpPath(config, answers);
  const reachableIds = new Set(jumpPath);
  const reachable = config.questions.filter((q) => reachableIds.has(q.id));

  const setAns = (qid: string, v: any) => {
    setLastQuestionId(qid);
    setAnswers((a) => {
      const next = { ...a, [qid]: v };
      // 0907 选项关联：源题切换后，目标题里已不可见的勾选立即清掉（提交 / 导出只保留仍可见的选项；服务端另有兜底）
      return pruneOptionLinkAnswers(config, next, new Set(computeVisibleQuestions(config, next)));
    });
  };
  // 0902 选项附属填空：文本存 answers._optionText[题号][选项值]（服务端只保留已选中且允许填空的选项文本）
  const setOptText = (qid: string, optValue: string, text: string) => {
    setLastQuestionId(qid);
    setAnswers((a) => withOptionText(a, qid, optValue, text));
  };
  // 1627 矩阵列附属填空：文本存 answers._matrixColText[题号][行标题]
  const setMatrixColText = (qid: string, row: string, text: string) => {
    setLastQuestionId(qid);
    setAnswers((a) => withMatrixColText(a, qid, row, text));
  };
  // 0902 多选题选数限制：返回第一道不合规题的提示文案（带题号），全部合规返回 null
  const selectLimitProblem = (qs: Array<Pick<Question, 'id' | 'type' | 'required' | 'params'>>): string | null => {
    const dmap = displayIndexMap(config.questions);
    for (const q of qs) {
      if (q.type !== 'multi' && q.type !== 'ranking') continue;
      const hint = selectLimitError(q, answers[q.id]);
      if (hint) return `第 ${dmap.get(q.id) ?? '?'} 题：${hint}`;
    }
    return null;
  };
  // 0903 填空题格式校验（姓名 / 手机号 / 身份证号）：返回第一道不合规题的提示（带题号或题干），全部合规返回 null
  const textFormatProblem = (qs: Array<Pick<Question, 'id' | 'type' | 'required' | 'params'> & { title?: any }>): string | null => {
    const dmap = displayIndexMap(config.questions);
    for (const q of qs) {
      const hint = textFormatError(q, answers[q.id]);
      if (!hint) continue;
      const no = dmap.get(q.id);
      return no ? `第 ${no} 题：${hint}` : `「${titleText(q.title)}」：${hint}`;
    }
    return null;
  };

  // 前置页：协议/须知正文/配图 + 独立前置填写项（settings.preface.questions）+ 标记「前置页」的题 +（可选）签名，一页完成；
  // 必须先完成前置页必答题（以及必填签名），才能勾选「我已知晓」进入问卷正文。
  const preface = config.settings?.preface;
  const sigCfg = config.settings?.signature;
  const sigOnPreface = !!(sigCfg?.enabled && sigCfg?.onPreface);
  const prefaceExtraQs = (preface?.questions ?? []) as any[];
  const prefaceQs = [...prefaceExtraQs, ...visible.filter((q) => q.onPreface)];
  const hasPrefacePage = mode === 'fill' && (!!(preface?.title || preface?.body || preface?.image) || prefaceQs.length > 0 || sigOnPreface);

  if (hasPrefacePage && !prefaceChecked) {
    const missingPreface = prefaceQs.filter((q) => q.required && !isNonAnswerType(q.type) && !isAnsweredFor(q, answers[q.id]));
    const sigMissing = !!(sigOnPreface && sigCfg?.required && !signature);
    const prefaceLimitMsg = selectLimitProblem(prefaceQs) ?? textFormatProblem(prefaceQs);
    const canAcknowledge = missingPreface.length === 0 && !sigMissing && !prefaceLimitMsg;
    return (
      <div className="runner">
        <div className="preface-page">
          {(preface?.title || preface?.body || preface?.image) && (
            <>
              <h1>{preface?.title ?? '前置须知'}</h1>
              {preface?.image && <img src={preface.image} alt="前置页图片" className="preface-image" />}
              {preface?.body && <pre className="preface-body">{preface.body}</pre>}
            </>
          )}
          {prefaceQs.length > 0 && (
            <ol className="runner-questions" ref={questionsRef} tabIndex={-1} style={{ marginTop: 16 }}>
              {prefaceQs.map((q) => (
                <li key={q.id} className="rq-item" data-question-id={q.id} tabIndex={-1}>
                  <div className="rq-no">{displayIndexMap(config.questions).get(q.id) ?? 0}</div>
                  <div className="rq-main">
                    <div className="rq-title">
                      <RichTextView value={q.title} />
                      {q.required && <span className="req">*</span>}
                    </div>
                    {q.description && <div className="rq-desc">{q.description}</div>}
                    <QuestionImages images={questionImages(q)} alt={titleText(q.title)} onOpen={(src, alt) => setTitleImage({ src, alt })} />
                    <QuestionInput
                      question={q}
                      value={answers[q.id]}
                      onChange={(v) => setAns(q.id, v)}
                      fullOptions={optionQuotasByQuestion[q.id]}
                      optionText={optionTextsOf(answers, q.id)}
                      onOptionTextChange={(ov, t) => setOptText(q.id, ov, t)}
                      matrixColText={matrixColTextsOf(answers, q.id)}
                      onMatrixColTextChange={(row, t) => setMatrixColText(q.id, row, t)}
                      allowedOptions={allowedOptionsOf(q, answers, visibleIdSet)}
                    />
                  </div>
                </li>
              ))}
            </ol>
          )}
          {sigOnPreface && (
            <div className="sig-block">
              <h3>签署人签名（{sigCfg?.mode === 'upload' ? '上传' : '手写'}）</h3>
              <SignaturePad mode={sigCfg?.mode ?? 'draw'} value={signature} onChange={setSignature} />
            </div>
          )}
          <label className={`checkbox ${!canAcknowledge ? 'is-disabled' : ''}`} style={{ marginTop: 14 }}>
            <input
              type="checkbox"
              checked={prefaceChecked}
              disabled={!canAcknowledge}
              onChange={(e) => e.target.checked && !reportInvalidScore() && setPrefaceChecked(true)}
            />
            我已知晓
          </label>
          {!canAcknowledge && (
            <div className="hint" style={{ marginTop: 8 }}>
              {missingPreface.length > 0 || sigMissing ? `请先完成前置页中的所有必答题${sigMissing ? '和必填签名' : ''}` : prefaceLimitMsg}
            </div>
          )}
        </div>
      </div>
    );
  }

  // 主页面题目：前置页题不重复显示（改答模式则全部集中在主页面，含独立前置填写项）
  // 0904 起：以跳题路径 path 为准；被跳过的题（jumpSkipped）不展示、不参与必填
  const mainQs = mode === 'edit' ? [...prefaceExtraQs, ...reachable.filter((q) => !q.onPreface)] : reachable.filter((q) => !q.onPreface);

  // 分页：有 pagebreak 时按页切换；多页模式下，逐页校验必填，未达标不允许翻页
  const pages = paginateQuestions(mainQs);
  const totalPages = pages.length;
  const hasPager = totalPages > 1;
  // 页码越界保护（display logic 改变导致当前页不存在时回落）：用派生值代替 useEffect，
  // 这里已在提前 return 之后，不能再调用 Hook。
  const safePageIndex = Math.min(Math.max(pageIndex, 0), Math.max(0, totalPages - 1));
  const currentPageQs = pages[safePageIndex] ?? [];
  const isFirstPage = safePageIndex === 0;
  const isLastPage = safePageIndex >= totalPages - 1;

  // 必填校验：path 内 + required + 非 statement/pagebreak；标题说明/分页栏不计答案（即使 required=true 也忽略）
  // 被跳过的题（jumpSkipped）不参与必填校验（提交时由后端裁剪答案兜底）
  const missingRequired = [...prefaceExtraQs, ...reachable].filter(
    (q) => q.required && !isNonAnswerType(q.type) && !isAnsweredFor(q, answers[q.id]),
  );
  const missingInCurrentPage = currentPageQs.filter(
    (q) => q.required && !isNonAnswerType(q.type) && !isAnsweredFor(q, answers[q.id]),
  );
  const pageCanAdvance = missingInCurrentPage.length === 0;
  const highlightedMissingIds = new Set(attemptedPage === safePageIndex ? missingInCurrentPage.map(q => q.id) : []);
  const sigRequired = config.settings?.signature?.required && !signature;
  const canSubmit = missingRequired.length === 0 && !sigRequired;

  // 邀请链接：/s/:id?invite=令牌 → 提交时带 x-invite-token 头
  const inviteToken = new URLSearchParams(location.search).get('invite') ?? undefined;

  const handleSubmit = async () => {
    if (!canSubmit || !surveyId) return;
    if (reportInvalidScore()) return;
    // 0902 / 0904 多选题选数限制 + 文本格式校验：按 path 内可见题校验；被跳过的题答案由后端 enforceAnswerRules 兜底清掉
    const limitMsg = selectLimitProblem(reachable) ?? textFormatProblem(reachable);
    if (limitMsg) {
      setError(limitMsg);
      window.alert(limitMsg);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // 地理位置签到：把成功取到的坐标随提交带上去，后端做距离兜底校验（防 Postman 直连 API）
      const geo =
        geoState.status === 'ok' && geoState.lat != null && geoState.lng != null
          ? { lat: geoState.lat, lng: geoState.lng, accuracy: geoState.accuracy, distance: geoState.distance }
          : undefined;
      // 改答模式：原地更新（保留 edit_token；飞书侧收到 event=update，不产生重复行）
      const r =
        mode === 'edit' && token
          ? // signature 显式传 null 才能在改答时清除签名（undefined 会被后端视作"保持原值"）
            await updateSubmissionByToken(token, { answers, signature })
          : await submitSurvey(
              surveyId,
              { answers, revision, signature: signature ?? undefined, geo },
              inviteToken ? { inviteToken } : undefined,
            );
      if (r.ok) {
        // 草稿已落库，清掉本机草稿（避免下次进入又回填已提交内容）
        if (mode !== 'edit' && surveyId) {
          try { localStorage.removeItem(`wj_draft_${surveyId}`); } catch {}
        }
        // 每人限填一次：记住本机已提交（拦截重复进入，同时保留改答入口）
        if (mode !== 'edit' && config.settings?.oncePerUser && r.editToken) {
          try { localStorage.setItem(`wj_submitted_${surveyId}`, r.editToken); } catch {}
        }
        navigate(`/s/${encodeURIComponent(surveyId)}/done`, {
          state: {
            editToken: r.editToken,
            surveyId,
            documentUrl: r.documentUrl ?? null,
            editAnswerEnabled: !!config.settings?.editAnswer?.enabled,
            resultPage: config.settings?.resultPage,
            redirectUrl: config.settings?.redirectUrl,
          },
        });
      } else {
        // 配额满 / 已停止 / IP 限次 / 邀请链接 / 服务端强校验
        const errMap: Record<string, string> = {
          total_quota_full: '名额已满，无法提交',
          option_quota_full: `选项配额已满：${r.detail?.optionValue ?? ''}`,
          submit_limit_full: `您已达到本问卷的提交次数上限（${r.detail?.limit ?? ''} 次）`,
          collect_stopped: '问卷已停止收集',
          collect_not_open: '问卷尚未开放',
          collect_ended: '问卷已结束',
          invite_required: '本问卷仅凭邀请链接填写',
          invite_invalid: '邀请令牌无效，请使用主办方提供的完整链接',
          already_submitted: '您已填写过本问卷（每人限填一次）',
          device_required: '请使用浏览器打开本问卷填写',
          edit_not_allowed: '本问卷未开启改答功能',
          edit_token_expired: '改答链接已过期',
          missing_required: '还有必填题未作答',
          select_count_invalid: '选择数量不符合要求',
          text_format_invalid: '填写格式不正确',
          signature_required: '请先完成电子签名',
          not_found: '提交记录不存在或已被删除',
          invalid_config: '问卷配置无效，无法提交',
          geo_required: '定位信息缺失，无法提交',
          geo_out_of_range: `您距签到点 ${r.detail?.distance ?? ''} 米，超出允许范围 ${r.detail?.radius ?? ''} 米`,
        };
        // 选数限制（0902）：服务端回传该题配置的提示文案
        // 选数限制（0902）/ 填空格式（0903）：服务端回传该题的提示文案，带上题号
        const hinted = (r.error === 'select_count_invalid' || r.error === 'text_format_invalid') && r.hint;
        const qNo = r.question ? displayIndexMap(config.questions).get(String(r.question)) : undefined;
        setError(hinted ? `${qNo ? `第 ${qNo} 题：` : ''}${String(r.hint)}` : errMap[r.error] || `提交失败：${r.error}`);
        // 刷新配额状态
        try {
          const q = await getQuota(surveyId);
          setQuota(q);
        } catch {}
      }
    } catch (e: any) {
      setError(e?.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="runner">
      <div className="runner-head">
        <Link to="/" className="runner-back">← 返回问卷管理</Link>
        <h1>{config.header.mainTitle}</h1>
        {config.header.subTitle && <p>{config.header.subTitle}</p>}
      </div>

      {draftNotice && (
        <div style={{ margin: '8px 0', padding: '10px 14px', background: 'var(--accent-soft-2)', border: '1px solid var(--accent)', borderRadius: 8, color: 'var(--accent-strong)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 14 }}>
          <span>已恢复上次填写的内容；附件与签名需重新上传/签署。</span>
        </div>
      )}

      <ol className="runner-questions" ref={questionsRef} tabIndex={-1}>
        {currentPageQs.map((q) => (
          <li
            key={q.id}
            data-question-id={q.id}
            tabIndex={-1}
            aria-labelledby={isNonAnswerType(q.type) ? undefined : `rq-title-${q.id}`}
            aria-describedby={highlightedMissingIds.has(q.id) ? `rq-missing-${q.id}` : undefined}
            className={`rq-item${isNonAnswerType(q.type) ? ' is-non-answer' : ''}${highlightedMissingIds.has(q.id) ? ' is-missing' : ''}`}
          >
            {!isNonAnswerType(q.type) && <div className="rq-no">{displayIndexMap(config.questions).get(q.id) ?? 0}</div>}
            <div className="rq-main">
              {q.type === 'statement' ? (
                <div className="rq-statement-block">
                  <RichTextView value={q.title} variant="statement" />
                  {q.description && <div className="rq-statement-sub">{q.description}</div>}
                  <QuestionImages images={questionImages(q)} alt={titleText(q.title)} onOpen={(src, alt) => setTitleImage({ src, alt })} />
                </div>
              ) : q.type === 'pagebreak' ? null : (
                <>
                  <div className="rq-title" id={`rq-title-${q.id}`}>
                    <RichTextView value={q.title} />
                    {q.required && <span className="req">*</span>}
                  </div>
                  {highlightedMissingIds.has(q.id) && (
                    <div className="rq-required-hint" id={`rq-missing-${q.id}`}>
                      此题为必答题，请完成填写后再继续。
                    </div>
                  )}
                  {q.description && <div className="rq-desc">{q.description}</div>}
                  <QuestionImages images={questionImages(q)} alt={titleText(q.title)} onOpen={(src, alt) => setTitleImage({ src, alt })} />
                  <QuestionInput
                    question={q}
                    value={answers[q.id]}
                    onChange={(v) => setAns(q.id, v)}
                    fullOptions={optionQuotasByQuestion[q.id]}
                    optionText={optionTextsOf(answers, q.id)}
                    onOptionTextChange={(ov, t) => setOptText(q.id, ov, t)}
                    matrixColText={matrixColTextsOf(answers, q.id)}
                    onMatrixColTextChange={(row, t) => setMatrixColText(q.id, row, t)}
                    allowedOptions={allowedOptionsOf(q, answers, visibleIdSet)}
                  />
                </>
              )}
            </div>
          </li>
        ))}
      </ol>

      {hasPager && (
        <div className="runner-pager">
          <button
            type="button"
            className="btn"
            onClick={() => {
              setError(null);
              setAttemptedPage(null);
              setPageIndex(Math.max(0, safePageIndex - 1));
              locateQuestion();
            }}
            disabled={isFirstPage}
            title={isFirstPage ? '已经是第一页' : '上一页'}
          >
            ← 上一页
          </button>
          <span className="runner-pager-info">第 {safePageIndex + 1} 页 / 共 {totalPages} 页</span>
          {isLastPage ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => { void handleSubmit(); }}
              disabled={!canSubmit || submitting}
            >
              {submitting ? '提交中…' : mode === 'edit' ? '保存修改' : '提交问卷'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                if (!pageCanAdvance) {
                  setError(null);
                  setAttemptedPage(safePageIndex);
                  locateQuestion(missingInCurrentPage[0].id);
                  return;
                }
                if (reportInvalidScore()) return;
                // 0902 多选题选数限制：本页有不合规的多选题时不允许翻页
                const limitMsg = selectLimitProblem(currentPageQs) ?? textFormatProblem(currentPageQs);
                if (limitMsg) {
                  setError(limitMsg);
                  window.alert(limitMsg);
                  return;
                }
                setError(null);
                setAttemptedPage(null);
                setPageIndex(Math.min(totalPages - 1, safePageIndex + 1));
                locateQuestion();
              }}
              title={pageCanAdvance ? '下一页' : '点击定位本页未填写的必答题'}
            >
              下一页 →
            </button>
          )}
        </div>
      )}

      {config.settings?.signature?.enabled && (mode === 'edit' || !sigOnPreface) && (!hasPager || isLastPage) && (
        <div className="sig-block">
          <h3>电子签（{config.settings.signature.mode === 'draw' ? '手写' : '上传'}）</h3>
          <SignaturePad
            mode={config.settings.signature.mode ?? 'draw'}
            value={signature}
            onChange={setSignature}
          />
        </div>
      )}

      {error && <div className="runner-error">{error}</div>}
      {hasPager ? (
        !pageCanAdvance && (
          <div className="hint hint-bad">本页还有 {missingInCurrentPage.length} 题必填未答</div>
        )
      ) : (
        missingRequired.length > 0 && (
          <div className="hint hint-bad">还有 {missingRequired.length} 题必填未答</div>
        )
      )}
      {titleImage && <ImageLightbox src={titleImage.src} alt={titleImage.alt} onClose={() => setTitleImage(null)} />}

      {!hasPager && (
        <div className="runner-actions">
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? '提交中…' : mode === 'edit' ? '保存修改' : '提交问卷'}
          </button>
        </div>
      )}
    </div>
  );
}

function DonePage({
  editToken,
  surveyId,
  documentUrl,
  editAnswerEnabled,
  resultPage,
  redirectUrl,
}: {
  editToken?: string;
  surveyId: string | null;
  documentUrl?: string | null;
  editAnswerEnabled?: boolean;
  resultPage?: { title?: string; body?: string };
  redirectUrl?: { enabled?: boolean; url?: string };
}) {
  const title = resultPage?.title || '提交成功 ✓';
  const body = resultPage?.body || '感谢您的填写，您的回答已记录。';
  const redirect = redirectUrl?.enabled && redirectUrl?.url ? redirectUrl.url : null;

  return (
    <div className="runner">
      <div className="runner-done">
        <h1>{title}</h1>
        <pre className="result-body">{body}</pre>
        {documentUrl && (
          <div className="edit-link-block">
            <p>您签署的文件已生成并存档，可随时查看或下载留存：</p>
            <p>
              <a className="btn" href={documentUrl} target="_blank" rel="noreferrer">查看签署文档</a>{' '}
              <a className="btn" href={`${documentUrl}?download=1`}>下载 PDF</a>
            </p>
          </div>
        )}
        {editToken && editAnswerEnabled && (
          <div className="edit-link-block">
            <p>您可凭以下专属链接修改本次提交（请妥善保存）：</p>
            <code className="edit-link">
              {window.location.origin}/edit/{editToken}
            </code>
          </div>
        )}
        <div className="runner-actions">
          {redirect && (
            <a href={redirect} className="btn btn-primary" target="_blank" rel="noreferrer">
              继续前往 →
            </a>
          )}{' '}
          {surveyId && (
            <Link to={`/s/${encodeURIComponent(surveyId)}`} className="btn">
              再填一份
            </Link>
          )}{' '}
          <Link to="/" className="btn">
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}

// 所有配了配额的选项（不论是否满额）——给 QuestionInput 渲染「剩余/总额」徽标用
function computeOptionQuotas(quota: QuotaState | null): Record<string, Array<{ value: string; limit: number; used: number; onQuotaFull: 'jumpNext' | 'end' }>> {
  const out: Record<string, any[]> = {};
  if (!quota) return out;
  for (const opt of quota.options) {
    if (!out[opt.questionId]) out[opt.questionId] = [];
    out[opt.questionId].push({
      value: opt.optionValue,
      limit: opt.limit,
      used: opt.used,
      onQuotaFull: opt.onQuotaFull,
    });
  }
  return out;
}

// 满额子集（remaining === 0）——给 onQuotaFull='end' 提前结束用
function computeFullOptions(optionQuotas: Record<string, Array<{ value: string; limit: number; used: number; onQuotaFull: 'jumpNext' | 'end' }>>): Record<string, Array<{ value: string; limit: number; used: number; onQuotaFull: 'jumpNext' | 'end' }>> {
  const out: Record<string, any[]> = {};
  for (const qid in optionQuotas) {
    for (const o of optionQuotas[qid]) {
      if (o.used < o.limit) continue; // 未满
      if (!out[qid]) out[qid] = [];
      out[qid].push(o);
    }
  }
  return out;
}

function isAnyFullEnd(map: Record<string, Array<{ onQuotaFull: string }>>): boolean {
  for (const qid in map) {
    for (const o of map[qid]) {
      if (o.onQuotaFull === 'end') return true;
    }
  }
  return false;
}

function isAnsweredFor(q: { type?: string; params?: any }, value: any): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  // 矩阵量表 / 矩阵填空（0902，0907 起可多列）/ 矩阵滑动（0907）：每行（每格）都答才算已答
  const matrixDone = matrixLikeAnswered(q, value);
  if (matrixDone !== null) return matrixDone;
  if (q.type === 'file') {
    return !!(typeof value === 'object' && (value.dataUrl || value.attachment));
  }
  // 文本题答案配图：{ text, images } 需有文字或至少一张图
  if (typeof value === 'object' && (typeof value.text === 'string' || Array.isArray(value.images))) {
    return String(value.text ?? '').trim() !== '' || (Array.isArray(value.images) && value.images.length > 0);
  }
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

/** Haversine 距离（米）：算两点球面距离，用于地理位置签到围栏判定 */
function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // 地球半径（米）
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** 题干配图（0903 起可多张）：缩略图横排，点击看大图 */
function QuestionImages({ images, alt, onOpen }: { images: string[]; alt: string; onOpen: (src: string, alt: string) => void }) {
  if (!images.length) return null;
  return (
    <div className="rq-images">
      {images.map((src, i) => (
        <button type="button" key={`${src.slice(0, 32)}-${i}`} onClick={() => onOpen(src, alt || `题图 ${i + 1}`)} title="点击查看大图" aria-label="点击查看大图">
          <img src={src} alt={alt || `题图 ${i + 1}`} />
        </button>
      ))}
    </div>
  );
}

/** 草稿过期时间：7 天（超过则不回填并清除本机草稿） */
const DRAFT_TTL = 7 * 24 * 60 * 60 * 1000;

/** 优先按最后修改的题号定位；旧草稿只能退回到保存页中最后一个有答案的题目。 */
function resumeDraftPosition(config: SurveyConfig, draft: PendingDraft): { pageIndex: number; questionId?: string } {
  const pickQuestion = (questions: Array<{ id: string; type: Question['type'] }>) => {
    const answerable = questions.filter(q => !isNonAnswerType(q.type));
    return answerable.find(q => q.id === draft.lastQuestionId)
      ?? [...answerable].reverse().find(q => Object.prototype.hasOwnProperty.call(draft.answers, q.id)
        || draft.answers[OPTION_TEXT_KEY]?.[q.id] || draft.answers[MATRIX_COL_TEXT_KEY]?.[q.id])
      ?? answerable[0];
  };
  const preface = config.settings?.preface;
  const visibleIds = new Set(computeVisibleQuestions(config, draft.answers));
  const prefaceQuestions = [...(preface?.questions ?? []), ...config.questions.filter(q => q.onPreface && visibleIds.has(q.id))];
  const hasPreface = !!(preface?.title || preface?.body || preface?.image || prefaceQuestions.length
    || (config.settings?.signature?.enabled && config.settings.signature.onPreface));
  if (hasPreface && !draft.prefaceChecked) {
    return { pageIndex: draft.pageIndex, questionId: pickQuestion(prefaceQuestions)?.id };
  }
  const reachableIds = new Set(computeJumpPath(config, draft.answers).path);
  const pages = paginateQuestions(config.questions.filter(q => !q.onPreface && reachableIds.has(q.id)));
  const targetPage = pages.findIndex(page => page.some(q => q.id === draft.lastQuestionId && !isNonAnswerType(q.type)));
  const pageIndex = targetPage >= 0 ? targetPage : Math.min(draft.pageIndex, Math.max(0, pages.length - 1));
  return { pageIndex, questionId: pickQuestion(pages[pageIndex] ?? [])?.id };
}

/** 写入/回填草稿共用的答案过滤：仅保留 validIds 内的题，跳过 file 题（dataUrl 体积大撑爆 localStorage 5MB 上限）。
 *  三类 key 统一处理：OPTION_TEXT_KEY / MATRIX_COL_TEXT_KEY（附属填空，按子 oid/qid 过滤）/ 普通题答案（qid 作 key）。
 *  写入侧（sanitizeDraftAnswers）与回填侧（useEffect 回填）共用此函数，避免两边分支漂移导致"存得进去读不回来"。 */
function pickDraftAnswers(src: Record<string, any>, validIds: Set<string>, fileIds: Set<string>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(src)) {
    if (k === OPTION_TEXT_KEY) {
      const opt = v && typeof v === 'object' && !Array.isArray(v) ? v : {};
      const cleanOpt: Record<string, any> = {};
      for (const [oid, ov] of Object.entries(opt)) {
        if (validIds.has(oid) && !fileIds.has(oid)) cleanOpt[oid] = ov;
      }
      if (Object.keys(cleanOpt).length) out[k] = cleanOpt;
    } else if (k === MATRIX_COL_TEXT_KEY) {
      const opt = v && typeof v === 'object' && !Array.isArray(v) ? v : {};
      const cleanOpt: Record<string, any> = {};
      for (const [qid, qv] of Object.entries(opt)) {
        if (validIds.has(qid) && !fileIds.has(qid)) cleanOpt[qid] = qv;
      }
      if (Object.keys(cleanOpt).length) out[k] = cleanOpt;
    } else if (validIds.has(k) && !fileIds.has(k)) {
      out[k] = v;
    }
  }
  return out;
}

/** 从 answers 拷贝并剔除 file 题答案，返回可安全持久化的草稿 */
function sanitizeDraftAnswers(config: SurveyConfig, answers: Record<string, any>): Record<string, any> {
  const questions = [...config.questions, ...(config.settings?.preface?.questions ?? [])];
  const validIds = new Set(questions.map((q) => q.id));
  const fileIds = new Set(questions.filter((q) => q.type === 'file').map((q) => q.id));
  return pickDraftAnswers(answers, validIds, fileIds);
}
